import { useEffect, useRef, useCallback, useMemo } from "react"

type ModifierKey = "ctrl" | "cmd" | "alt" | "shift" | "meta"

interface KeyCombo {
    key: string
    modifiers?: ModifierKey[]
}

interface ShortcutConfig {
    // For simultaneous key press (e.g., Ctrl+S)
    combo?: KeyCombo
    // For sequential key press (e.g., g -> i)
    sequence?: string[]
    handler: () => void
    // Optional: preventDefault on the event
    preventDefault?: boolean
    // Optional: only trigger when no input is focused
    ignoreInputs?: boolean
}

interface UseKeyboardShortcutOptions {
    // Enable/disable all shortcuts
    enabled?: boolean
    // Timeout for key sequences (default: 1000ms)
    sequenceTimeout?: number
}

/**
 * Custom hook for handling keyboard shortcuts
 * Supports both key combinations (Ctrl+S) and key sequences (g -> i)
 *
 * @example
 * // Single shortcut
 * useKeyboardShortcut({
 *   combo: { key: 's', modifiers: ['ctrl'] },
 *   handler: () => console.log('Save!')
 * })
 *
 * @example
 * // Multiple shortcuts
 * useKeyboardShortcut([
 *   {
 *     combo: { key: 's', modifiers: ['ctrl'] },
 *     handler: () => console.log('Save!')
 *   },
 *   {
 *     sequence: ['g', 'i'],
 *     handler: () => console.log('Go to issues!')
 *   }
 * ])
 */
export const useKeyboardShortcut = (
    shortcuts: ShortcutConfig | ShortcutConfig[],
    options: UseKeyboardShortcutOptions = {}
) => {
    const { enabled = true, sequenceTimeout = 1000 } = options
    const sequenceRef = useRef<string[]>([])
    const sequenceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    )

    const normalizedShortcuts = useMemo(
        () => (Array.isArray(shortcuts) ? shortcuts : [shortcuts]),
        [shortcuts]
    )

    const isInputFocused = useCallback(() => {
        const activeElement = document.activeElement
        if (!activeElement) return false

        const tagName = activeElement.tagName?.toLowerCase() || ""
        const isContentEditable =
            activeElement.getAttribute("contenteditable") === "true"

        return (
            tagName === "input" ||
            tagName === "textarea" ||
            tagName === "select" ||
            isContentEditable
        )
    }, [])

    const matchesCombo = useCallback(
        (event: KeyboardEvent, combo: KeyCombo): boolean => {
            const { key, modifiers = [] } = combo

            // Check if the main key matches
            if (event.key.toLowerCase() !== key.toLowerCase()) {
                return false
            }

            // Check modifiers
            const modifierChecks = {
                ctrl: event.ctrlKey,
                cmd: event.metaKey, // Cmd on Mac
                meta: event.metaKey,
                alt: event.altKey,
                shift: event.shiftKey
            }

            // Check that all required modifiers are pressed
            for (const modifier of modifiers) {
                if (!modifierChecks[modifier]) {
                    return false
                }
            }

            // Check that no extra modifiers are pressed
            const allModifiers: ModifierKey[] = [
                "ctrl",
                "cmd",
                "alt",
                "shift",
                "meta"
            ]
            for (const modifier of allModifiers) {
                if (!modifiers.includes(modifier) && modifierChecks[modifier]) {
                    // Special case: cmd and meta are the same
                    if (modifier === "cmd" && modifiers.includes("meta"))
                        continue
                    if (modifier === "meta" && modifiers.includes("cmd"))
                        continue
                    return false
                }
            }

            return true
        },
        []
    )

    const clearSequence = useCallback(() => {
        sequenceRef.current = []
        if (sequenceTimerRef.current) {
            clearTimeout(sequenceTimerRef.current)
        }
    }, [])

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (!enabled) return

            // Check each shortcut
            for (const shortcut of normalizedShortcuts) {
                // Skip if ignoreInputs is true and an input is focused
                if (shortcut.ignoreInputs && isInputFocused()) {
                    continue
                }

                // Check for combo match
                if (shortcut.combo && matchesCombo(event, shortcut.combo)) {
                    if (shortcut.preventDefault) {
                        event.preventDefault()
                    }
                    shortcut.handler()
                    clearSequence() // Clear any ongoing sequence
                    return
                }

                // Check for sequence match
                if (shortcut.sequence && shortcut.sequence.length > 0) {
                    // Add current key to sequence
                    sequenceRef.current.push(event.key)

                    // Clear previous timer
                    if (sequenceTimerRef.current) {
                        clearTimeout(sequenceTimerRef.current)
                    }

                    // Set new timer to clear sequence
                    sequenceTimerRef.current = setTimeout(
                        clearSequence,
                        sequenceTimeout
                    )

                    // Check if current sequence matches any shortcut sequence
                    const currentSequence = sequenceRef.current.join("")
                    const targetSequence = shortcut.sequence.join("")

                    if (currentSequence === targetSequence) {
                        if (shortcut.preventDefault) {
                            event.preventDefault()
                        }
                        shortcut.handler()
                        clearSequence()
                        return
                    } else if (!targetSequence.startsWith(currentSequence)) {
                        // If current sequence can't lead to target, keep only last key
                        sequenceRef.current = [event.key]
                    }
                }
            }
        },
        [
            enabled,
            normalizedShortcuts,
            isInputFocused,
            matchesCombo,
            clearSequence,
            sequenceTimeout
        ]
    )

    useEffect(() => {
        if (!enabled) return

        window.addEventListener("keydown", handleKeyDown)

        return () => {
            window.removeEventListener("keydown", handleKeyDown)
            clearSequence()
        }
    }, [enabled, handleKeyDown, clearSequence])

    // Return a function to programmatically clear the sequence
    return { clearSequence }
}
